/**
 * Settings Page
 * User and organization settings
 * Tabbed interface with profile, organization, notifications, and security
 */

import React, { Suspense, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  User,
  Building2,
  Bell,
  Shield,
  Mail,
  Plug,
  Save,
  LogOut,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  NotificationPreferenceKey,
  NotificationPreferences,
  normalizeNotificationPreferences,
} from '@/types/notificationPreferences';
import { toast } from 'sonner';
import { MFAEnrollment } from '@/components/auth/MFAEnrollment';
import { EmailScanningSettings } from '@/features/email-scanning/components/EmailScanningSettings';
import { OtterIntegrationSettings } from '@/features/otter/components/OtterIntegrationSettings';
import { UnassignedMeetingNotes } from '@/features/otter/components/UnassignedMeetingNotes';
import { FirefliesIntegrationSettings } from '@/features/fireflies/components/FirefliesIntegrationSettings';
import { UnassignedFirefliesNotes } from '@/features/fireflies/components/UnassignedFirefliesNotes';
import { GranolaIntegrationSettings } from '@/features/granola/components/GranolaIntegrationSettings';
import { UnassignedGranolaNotes } from '@/features/granola/components/UnassignedGranolaNotes';
import { OrganizationMembersTab } from '@/components/settings/OrganizationMembersTab';
import { AvatarUploadControl } from '@/components/profile/AvatarUploadControl';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc } from 'firebase/firestore';
import { functions, db } from '@/lib/firebase';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import {
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';

// Lazy: the card carries the setup guide's question schema, which the rest of Settings does not need.
// lazyWithRetry: after a deploy the chunk URL can be stale, which would otherwise leave this card on its fallback.
const ProfileSummaryCard = lazyWithRetry(() =>
  import('@/features/onboarding/components/ProfileSummaryCard').then((module) => ({
    default: module.ProfileSummaryCard,
  })),
);

const SETTINGS_TABS = ['profile', 'organization', 'notifications', 'security', 'email', 'integrations'] as const;
type SettingsTab = typeof SETTINGS_TABS[number];

function getValidSettingsTab(tab: string | null): SettingsTab {
  return SETTINGS_TABS.includes(tab as SettingsTab) ? (tab as SettingsTab) : 'profile';
}

export const Settings: React.FC = () => {
  const { user, userProfile, logout, refreshProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const initialTab = getValidSettingsTab(searchParams.get('tab'));
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Profile state
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');

  // Organization state - load from user profile
  const [orgName, setOrgName] = useState(userProfile?.companyName || '');
  const [orgWebsite, setOrgWebsite] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Notification state
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>(
    DEFAULT_NOTIFICATION_PREFERENCES
  );
  const isOrganizationAdmin = userProfile?.role === 'admin' && userProfile?.permissions?.canManageUsers === true;
  const currentAvatarUrl = userProfile?.avatarUrl || userProfile?.photoURL || user?.photoURL || null;

  useEffect(() => {
    const tabFromUrl = getValidSettingsTab(searchParams.get('tab'));
    setActiveTab(tabFromUrl);
  }, [searchParams]);

  useEffect(() => {
    setNotificationPreferences(
      normalizeNotificationPreferences(
        userProfile?.notificationPreferences || userProfile?.preferences?.notifications
      )
    );
  }, [userProfile?.notificationPreferences, userProfile?.preferences]);

  // Analytics consent (opt-in, default false). The reader (hasAnalyticsConsent /
  // AnalyticsBridge) already gates analytics on preferences.privacy.analyticsConsent;
  // this is the write path.
  const [analyticsConsent, setAnalyticsConsent] = useState<boolean>(
    userProfile?.preferences?.privacy?.analyticsConsent === true
  );

  useEffect(() => {
    setAnalyticsConsent(userProfile?.preferences?.privacy?.analyticsConsent === true);
  }, [userProfile?.preferences]);

  const handleTabChange = (value: string) => {
    const nextTab = getValidSettingsTab(value);
    setActiveTab(nextTab);

    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === 'profile') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', nextTab);
    }

    const search = nextParams.toString();
    navigate(
      {
        pathname: '/settings',
        search: search ? `?${search}` : '',
      },
      { replace: true }
    );
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Update Firebase Auth profile (display name)
      if (displayName !== user.displayName) {
        await updateProfile(user, { displayName });
      }

      // Update Firestore user profile via Cloud Function
      const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
      await updateUserProfile({
        userId: user.uid,
        displayName: displayName,
      });

      await refreshProfile();
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrganization = async () => {
    if (!user) return;

    setLoading(true);
    try {
      await setDoc(doc(db, 'users', user.uid), {
        companyName: orgName.trim(),
        updatedAt: new Date(),
      }, { merge: true });
      await refreshProfile();
      toast.success('Organization settings updated');
    } catch (error) {
      console.error('Failed to update organization settings:', error);
      toast.error('Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!user) return;

    setLoading(true);
    try {
      await setDoc(doc(db, 'users', user.uid), {
        preferences: {
          ...(userProfile?.preferences || {}),
          notifications: notificationPreferences,
        },
        updatedAt: new Date(),
      }, { merge: true });
      await refreshProfile();
      toast.success('Notification preferences updated');
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      toast.error('Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrivacy = async () => {
    if (!user) return;

    setLoading(true);
    try {
      await setDoc(doc(db, 'users', user.uid), {
        preferences: {
          ...(userProfile?.preferences || {}),
          privacy: {
            ...(userProfile?.preferences?.privacy || {}),
            analyticsConsent,
          },
        },
        updatedAt: new Date(),
      }, { merge: true });
      await refreshProfile();
      toast.success(analyticsConsent ? 'Product analytics enabled' : 'Product analytics disabled');
    } catch (error) {
      console.error('Failed to update privacy preferences:', error);
      toast.error('Failed to update privacy preferences');
    } finally {
      setLoading(false);
    }
  };

  const updateNotificationPreference = (
    key: NotificationPreferenceKey,
    checked: boolean
  ) => {
    setNotificationPreferences((current) => ({
      ...current,
      [key]: checked,
    }));
  };

  const renderNotificationToggle = (
    key: NotificationPreferenceKey,
    title: string,
    description: string,
    options?: { disabled?: boolean; accent?: string }
  ) => (
    <div className={`flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between ${options?.disabled ? 'opacity-60' : ''}`}>
      <div className="space-y-1">
        <p className="font-medium text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
        {options?.accent && (
          <p className="text-xs font-medium text-primary">{options.accent}</p>
        )}
      </div>
      <Switch
        checked={notificationPreferences[key]}
        onCheckedChange={(checked) => updateNotificationPreference(key, checked)}
        disabled={options?.disabled}
        aria-label={title}
      />
    </div>
  );

  const handleUpdatePassword = async () => {
    if (!user || !user.email) {
      toast.error('You must be signed in to change your password');
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password updated successfully');
    } catch (error: unknown) {
      console.error('Password update error:', error);
      const authError = error as { code?: string; message?: string };
      if (authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
        toast.error('Current password is incorrect');
      } else if (authError.code === 'auth/weak-password') {
        toast.error('New password is too weak. Use at least 6 characters.');
      } else if (authError.code === 'auth/requires-recent-login') {
        toast.error('Please sign out and sign back in, then try again');
      } else {
        toast.error(authError.message || 'Failed to update password');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-10 max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your account and organization preferences
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6">
            <TabsTrigger value="profile" className="gap-2" aria-label="Profile">
              <User className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="organization" className="gap-2" aria-label="Organization">
              <Building2 className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Organization</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2" aria-label="Notifications">
              <Bell className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Notifications</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2" aria-label="Security">
              <Shield className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Security</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-2" aria-label="Email">
              <Mail className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Email</span>
            </TabsTrigger>
            <TabsTrigger value="integrations" className="gap-2" aria-label="Integrations">
              <Plug className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Integrations</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
                <CardDescription>
                  Update your personal information and preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Avatar */}
                <AvatarUploadControl
                  currentAvatarUrl={currentAvatarUrl}
                  displayName={displayName || userProfile?.displayName || user?.displayName}
                  email={email || userProfile?.email || user?.email}
                />

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="displayName">Full Name</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    Your email is used for login and notifications
                  </p>
                </div>

                {/* Role */}
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input
                    id="role"
                    value={userProfile?.role ? userProfile.role.charAt(0).toUpperCase() + userProfile.role.slice(1) : 'Analyst'}
                    disabled
                    className="bg-muted/50"
                  />
                  <p className="text-xs text-muted-foreground">
                    Contact your administrator to change your role
                  </p>
                </div>

                <div className="flex flex-col gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="darkMode" className="font-semibold text-foreground">Appearance</Label>
                    <p className="text-sm text-muted-foreground">Use dark mode in the authenticated workspace on this device.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label htmlFor="darkMode" className="text-sm text-muted-foreground">Dark mode</Label>
                    <Switch id="darkMode" checked={theme === 'dark'} onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')} />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveProfile} disabled={loading}>
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Investing profile captured by the setup guide */}
            <div className="mt-6">
              <Suspense
                fallback={
                  <Card className="border-border bg-card">
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground" role="status">
                        Loading your investing profile…
                      </p>
                    </CardContent>
                  </Card>
                }
              >
                <ProfileSummaryCard />
              </Suspense>
            </div>

            {/* Sign Out Section */}
            <Card className="border-border bg-card mt-6">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-foreground">Sign Out</h3>
                    <p className="text-sm text-muted-foreground">
                      Sign out of your account on this device
                    </p>
                  </div>
                  <Button variant="outline" onClick={handleSignOut} className="text-foreground">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organization Tab */}
          <TabsContent value="organization">
            <div className="space-y-6">
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle>Organization Settings</CardTitle>
                  <CardDescription>
                    Manage your firm's information and defaults
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Organization Name */}
                  <div className="space-y-2">
                    <Label htmlFor="orgName">Organization Name</Label>
                    <Input
                      id="orgName"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder="Your Firm Name"
                    />
                  </div>

                  {/* Website */}
                  <div className="space-y-2">
                    <Label htmlFor="orgWebsite">Website</Label>
                    <Input
                      id="orgWebsite"
                      type="url"
                      value={orgWebsite}
                      onChange={(e) => setOrgWebsite(e.target.value)}
                      placeholder="https://yourfirm.com"
                    />
                  </div>

                  {/* Logo Upload */}
                  <div className="space-y-2">
                    <Label>Firm Logo</Label>
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                      <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-2">Upload your firm's logo</p>
                      <Button variant="outline" size="sm">
                        Choose File
                      </Button>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={handleSaveOrganization} disabled={loading}>
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <OrganizationMembersTab
                isAdmin={isOrganizationAdmin}
                currentUserId={user?.uid}
              />
            </div>
          </TabsContent>


          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>
                  Tune high-signal alerts for deal teams without flooding inboxes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-start gap-3">
                    <Bell className="mt-0.5 h-5 w-5 text-primary" />
                    <div className="space-y-1">
                      <p className="font-medium text-foreground">Recommended deal-team setup</p>
                      <p className="text-sm text-muted-foreground">
                        Keep in-app notifications on for all workstreams. Use email for mentions,
                        access changes, failures, and weekly summaries unless you explicitly want
                        every workflow update in your inbox.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      Delivery
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      These settings control whether backend notification producers create in-app
                      records and email messages for your account.
                    </p>
                  </div>
                  {renderNotificationToggle(
                    'inAppNotifications',
                    'In-app notification center',
                    'Show real-time alerts in the top-right notification bell.',
                    { accent: 'Recommended on' }
                  )}
                  {renderNotificationToggle(
                    'emailNotifications',
                    'Email notifications',
                    'Send email for the notification categories enabled below.'
                  )}
                  {renderNotificationToggle(
                    'emailCriticalOnly',
                    'Email critical items only',
                    'Limit email to mentions, team access changes, failed workflows, and similar urgent items.',
                    {
                      disabled: !notificationPreferences.emailNotifications,
                      accent: 'Recommended on for active deal teams',
                    }
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      Deal And Portfolio Alerts
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Separate deal diligence noise from portfolio-company monitoring so each team
                      member can follow the workstreams they own.
                    </p>
                  </div>
                  {renderNotificationToggle(
                    'dealAlerts',
                    'Deal activity and milestones',
                    'Notify me when a deal reaches material diligence or investment committee milestones.'
                  )}
                  {renderNotificationToggle(
                    'portfolioAlerts',
                    'Portfolio company updates',
                    'Notify me about portco reporting, KPI, covenant, and operating-performance alerts.'
                  )}
                  {renderNotificationToggle(
                    'workflowAlerts',
                    'AI workflow and deliverable status',
                    'Notify me when rapid deal screens, research, models, memos, and decks start, finish, or fail.'
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      Collaboration
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      These are the highest-signal coordination alerts for deal-team handoffs.
                    </p>
                  </div>
                  {renderNotificationToggle(
                    'commentsAndMentions',
                    'Comments, replies, and @mentions',
                    'Notify me when someone mentions me or replies in a thread I participated in.'
                  )}
                  {renderNotificationToggle(
                    'teamUpdates',
                    'Team and access changes',
                    'Notify me when I am added to a deal, portfolio company, or restricted workstream.'
                  )}
                  {renderNotificationToggle(
                    'documentUpdates',
                    'Document activity',
                    'Notify me when important source documents are shared, processed, or need attention.'
                  )}
                </div>

                <div className="space-y-3">
                  {renderNotificationToggle(
                    'weeklyDigest',
                    'Weekly deal-team digest',
                    'Send a weekly email summary of unread and recent alerts across deals and portcos.',
                    { disabled: !notificationPreferences.emailNotifications }
                  )}
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveNotifications} disabled={loading}>
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <div className="space-y-6">
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                  <CardDescription>
                    Manage your password and security preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Change Password */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-foreground">Change Password</h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword">Current Password</Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          placeholder="Enter current password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          disabled={passwordLoading}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">New Password</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          disabled={passwordLoading}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={passwordLoading}
                        />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleUpdatePassword}
                      disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                    >
                      {passwordLoading ? 'Updating...' : 'Update Password'}
                    </Button>
                  </div>

                  {/* Two-Factor Authentication */}
                  <div className="space-y-3 pt-6 border-t">
                    <MFAEnrollment />
                  </div>
                </CardContent>
              </Card>

              {/* Privacy & Data */}
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle>Privacy &amp; Data</CardTitle>
                  <CardDescription>
                    Control how your anonymous product-usage data is handled
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 p-4 bg-muted/30 border border-border rounded-lg">
                    <div className="space-y-1">
                      <Label htmlFor="analyticsConsent" className="font-semibold text-foreground">
                        Product usage analytics
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Share anonymous, aggregated usage data (never document contents,
                        financials, or personal information) to help improve the product.
                        Your browser&apos;s Do Not Track / Global Privacy Control signal is
                        always honored.
                      </p>
                    </div>
                    <Switch
                      id="analyticsConsent"
                      checked={analyticsConsent}
                      onCheckedChange={setAnalyticsConsent}
                      disabled={loading}
                      className="mt-1 shrink-0"
                    />
                  </div>
                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={handleSavePrivacy} disabled={loading}>
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? 'Saving...' : 'Save Privacy Settings'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Danger Zone */}
              <Card className="border-destructive/20 bg-destructive/10">
                <CardHeader>
                  <CardTitle className="text-destructive">Danger Zone</CardTitle>
                  <CardDescription className="text-destructive/80">
                    Irreversible actions that affect your account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-card border border-destructive/20 rounded-lg">
                    <div>
                      <p className="font-semibold text-foreground">Export Your Data</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Download all your projects, documents, and analysis data
                      </p>
                    </div>
                    <Button variant="outline" className="w-full sm:w-auto">
                      Export Data
                    </Button>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-card border border-destructive/20 rounded-lg">
                    <div>
                      <p className="font-semibold text-destructive">Delete Account</p>
                      <p className="text-sm text-destructive/80 mt-1">
                        Permanently delete your account and all associated data
                      </p>
                    </div>
                    <Button variant="destructive" className="w-full sm:w-auto">
                      Delete Account
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email">
            <EmailScanningSettings />
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations">
            <div className="space-y-6">
              <GranolaIntegrationSettings />
              <UnassignedGranolaNotes />
              <FirefliesIntegrationSettings />
              <UnassignedFirefliesNotes />
              <OtterIntegrationSettings />
              <UnassignedMeetingNotes />
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div >
  );
};
